/**
 * Tema sağlayıcı — açık/koyu mod.
 *
 * Tek iş yapar: seçili modu `<html>` üzerinde `dark` sınıfı olarak yayınlar
 * ve antd'ye eşleşen token setini verir. Renklerin kendisi burada değil,
 * `src/index.css` (CSS değişkenleri) ve `src/theme/antdTheme.js` içinde.
 *
 * Sıralama: kullanıcı seçimi (localStorage) → işletim sistemi tercihi.
 * Kullanıcı bir kez seçim yaparsa işletim sistemi tercihi artık izlenmez.
 */
import { ConfigProvider } from "antd";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { buildAntdTheme } from "./antdTheme";

const STORAGE_KEY = "boyahane.theme";
const ThemeContext = createContext({ mode: "light", setMode: () => {} });

/** Depolanan geçerli bir seçim yoksa null döner. */
const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    // Gizli sekmede localStorage erişimi hata fırlatabilir.
    return null;
  }
};

const systemMode = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => readStored() ?? systemMode());
  const [pinned, setPinned] = useState(() => readStored() !== null);

  // `dark` sınıfı Tailwind'in darkMode:"class" ayarının kancası.
  //
  // Değişim sırasında geçişler kapatılır: index.css'te satır hover parlamasını
  // kapatmak için `transition-duration: 9999s` hilesi var; bu, tema değişince
  // arka planı da dondurup hücreyi eski renginde bırakıyor.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("theme-switching");
    root.classList.toggle("dark", mode === "dark");
    root.dataset.theme = mode;

    // Yeni değerler boyandıktan SONRA geçişleri geri aç.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => root.classList.remove("theme-switching"));
    });
    return () => {
      cancelAnimationFrame(raf);
      root.classList.remove("theme-switching");
    };
  }, [mode]);

  // Kullanıcı henüz seçim yapmadıysa işletim sistemini izle.
  useEffect(() => {
    if (pinned) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setModeState(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pinned]);

  const setMode = useCallback((next) => {
    setModeState(next);
    setPinned(true);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Yazılamazsa tercih yalnız bu oturumda geçerli olur.
    }
  }, []);

  const toggle = useCallback(
    () => setMode(mode === "dark" ? "light" : "dark"),
    [mode, setMode]
  );

  const value = useMemo(
    () => ({ mode, setMode, toggle, isDark: mode === "dark" }),
    [mode, setMode, toggle]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={buildAntdTheme(mode)}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
